import React, { useState, useEffect } from 'react'
import { Link, generatePath } from 'react-router-dom'
import Input from 'components/input'
import Modal, { ModalProps } from 'components/modal'
import { IProcessObject } from 'types'
import api from 'helpers/api'
import Editor from 'components/editor'
import Button from 'components/button'
import DocumentComponent from 'components/document'
import { PERMISSIONS } from 'helpers/auth'
import { useAuth } from 'hooks/auth'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter, validateSourceUrl } from 'helpers/utils'
import useProject from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { PROFILE, DOCUMENT } from 'routes'
import Skeleton from 'components/skeleton'

interface TemplatesProps extends ModalProps {
  /** The sidebar folder to file the new document (and the tables copied with it) in. */
  folderId?: string | null
}

const Templates: React.FC<TemplatesProps> = ({ id, open, setOpen, folderId }) => {
  const { user } = useAuth()
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [searchText, setSearchText] = useState<string>('')
  const [templates, setTemplates] = useState<Partial<IProcessObject>[]>()
  const [selectedTemplate, setSelectedTemplate] = useState<{
    templateId: string
    projectId: string
  }>()

  const templateStyle = {}
  const templateClass =
    'items-center rounded cursor-pointer transition-all border   border-divider-strong box-border hover:shadow-heavy hover:-translate-x-px hover:-translate-y-px bg-surface'

  useEffect(() => {
    if (user && user.publicId !== '')
      api
        .getTemplates()
        .then((response) => {
          setTemplates(response.data)
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
  }, [user])

  const duplicateProcess = (templateId: string) => {
    const template = templates?.find((t) => t.publicId === templateId)
    api
      .duplicateProcess({
        publicId: templateId,
        targetProjectId: project.publicId,
        duplicatePermissions: true,
        lockResource: template?.lockedTemplate ? true : false,
        // Only a folder is sent: without one the copy goes where the server files copies by default.
        folderId: folderId ?? undefined
      })
      .then(() => {
        setSnackbarMessage({
          status: 'success',
          message: 'Duplication has started. You will receive an email once it has been complete.'
        })
        setOpen(false)
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      title={
        !selectedTemplate ? (
          <Input
            className="text-base-sm"
            type="text"
            placeholder={'Search for a template'}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        ) : (
          <Button internalType="outline" onClick={() => setSelectedTemplate(undefined)}>
            ← Back
          </Button>
        )
      }
      removePadding={true}
    >
      {!selectedTemplate ? (
        <div>
          <div className="flex text-base-sm">
            <div className="overflow-y-auto w-full py-[30px] px-10">
              {templates ? (
                <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(400px,1fr))]">
                  {templates
                    .filter((template) =>
                      searchText ? template.name?.toLowerCase().includes(searchText.toLowerCase()) : true
                    )
                    .map((template, templateIndex) => {
                      return (
                        <div
                          role="button"
                          aria-label="Template"
                          key={templateIndex}
                          className={templateClass}
                          style={templateStyle}
                          tabIndex={0}
                          onClick={() => {
                            if (template && template.publicId && template.projectPublicId) {
                              setSelectedTemplate({
                                templateId: template.publicId,
                                projectId: template.projectPublicId
                              })
                            }
                          }}
                        >
                          <div className="flex relative w-full overflow-hidden justify-center items-center h-[100px]">
                            {template.logo && (
                              <img
                                src={validateSourceUrl(template.logo)}
                                className="absolute w-full h-full object-contain"
                                alt={template.name || 'Template logo'}
                              />
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-lg box-border border-t border-divider-strong mb-2.5 p-2.5">
                              {template.name}
                            </div>
                            <div className="overflow-y-auto b p-[5px] h-[200px]">
                              <Editor
                                databaseDoc={template.description}
                                readOnly={true}
                                editorId={templateIndex.toString()}
                                border={false}
                                resources={[]}
                              />
                            </div>
                            {template && template.createdBy && (
                              <div className="box-border border-t border-divider-strong py-[15px] px-2.5">
                                <div className="flex items-center">
                                  <img
                                    className="rounded-full h-[25px] w-[25px]"
                                    src={validateSourceUrl(template.createdBy.profilePicture)}
                                    alt={template.createdBy.profilePicture || 'Anonymous user'}
                                  />
                                  <div className="text-muted ml-[5px]">
                                    Created by{' '}
                                    <Link
                                      to={generatePath(PROFILE, {
                                        userId: template.createdBy.firebaseUserId ?? '#',
                                        tab: 'information'
                                      })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {template.createdBy.name}
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                </div>
              ) : (
                <div>
                  <div className="font-bold text-2xl mb-4">
                    <Skeleton className="h-8 w-[200px]" />
                  </div>
                  <div className="grid grid-cols-2 max-lg:grid-cols-1 gap-4">
                    <Skeleton className="h-[100px]" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="h-[50vh]">
            <DocumentComponent
              processPublicId={selectedTemplate.templateId}
              permissionCap={PERMISSIONS.viewer}
              templateRender={true}
            />
          </div>

          <div className="flex justify-end border-t-2 border-divider p-5">
            <Button
              to={generatePath(DOCUMENT, {
                projectId: selectedTemplate.projectId,
                processId: selectedTemplate.templateId
              })}
              internalType="outline"
              onClick={() => {
                setOpen(false)
                setSelectedTemplate(undefined)
              }}
            >
              View In {capitaliseFirstLetter(PROJECT)}
            </Button>
            <Button
              className="ml-2.5"
              internalType="accept"
              onClick={() => duplicateProcess(selectedTemplate.templateId)}
              disabled={!user || project.publicId === ''}
            >
              {user
                ? project.publicId !== ''
                  ? 'Use This Template'
                  : `Go To A ${capitaliseFirstLetter(PROJECT)} To Use Template`
                : 'Sign In To Use This Template'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
export default Templates
